/**
 * Camera-step helpers for stream addresses (ENG-1924).
 *
 * Installers usually arrive with a working stream URL
 * (`rtsps://user:pass@172.16.254.237:654/<GUID>/live`), but the wizard asks for
 * it in pieces: Host (`ip`), RTSP port (`rtsp`) and Stream Parameter
 * (`channel`). The connector builds `{ip}:{rtsp}{channel}` with no separator,
 * so a whole URL pasted into Stream Parameter produced
 * `10.101.35.104:554rtsps://...` for every camera on a Genesis site
 * (connector-51646). These helpers split a pasted URL into the fields, flag
 * values the connector cannot use, and render the address it will dial.
 */

export interface ParsedStreamUrl {
  protocol: string;
  host: string;
  port: string;
  path: string;
  username: string;
  password: string;
}

const SCHEME = /^(rtsps?|https?):\/\/(.*)$/is;

// RTSPS has no single real-world default: TLS-capable cameras use 554, the
// Genetec Media Gateway 654 (ENG-2042). 554 is the likelier guess; the hints
// below tell Genetec users to put :654 in the URL.
const DEFAULT_PORTS: Record<string, string> = {
  rtsp: "554",
  rtsps: "554",
  http: "80",
  https: "443",
};

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * Split `rtsp(s)://user:pass@host:port/path` into wizard fields, or null.
 *
 * Camera passwords routinely contain `@`, `#` and `:` (a Genesis camera had
 * `ctu@t32026!@#A`), and installers paste them unencoded. So the credentials
 * end at the LAST `@` before the path, and everything after the first `:` in
 * them is the password. A `#` inside the credentials is part of the
 * password, never a URL fragment.
 */
export const parseStreamUrl = (raw: string): ParsedStreamUrl | null => {
  const match = SCHEME.exec((raw || "").trim());
  if (!match) return null;
  const protocol = match[1].toLowerCase();
  const rest = match[2];

  // The path starts at the first "/" after the scheme. A password with "/"
  // in it is the one shape this cannot handle; ask for it encoded (%2F).
  const slash = rest.indexOf("/");
  const authority = slash === -1 ? rest : rest.slice(0, slash);
  let path = slash === -1 ? "" : rest.slice(slash);

  const at = authority.lastIndexOf("@");
  const userinfo = at === -1 ? "" : authority.slice(0, at);
  const hostPort = at === -1 ? authority : authority.slice(at + 1);

  const colon = userinfo.indexOf(":");
  const username = colon === -1 ? userinfo : userinfo.slice(0, colon);
  const password = colon === -1 ? "" : userinfo.slice(colon + 1);

  const hostMatch = /^([^:?#]+)(?::(\d+))?$/.exec(hostPort);
  if (!hostMatch) return null;
  const [, host, port] = hostMatch;

  if (path && !path.startsWith("/")) path = `/${path}`;
  return {
    protocol,
    host,
    port: port || DEFAULT_PORTS[protocol] || "",
    path,
    username: safeDecode(username),
    password: safeDecode(password),
  };
};

const GUID_LIVE_PATH =
  /^\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/live/i;

/** Split off the part after the scheme into authority and path, or null. */
const splitAuthority = (
  raw: string
): { authority: string; path: string } | null => {
  const match = SCHEME.exec((raw || "").trim());
  if (!match) return null;
  const rest = match[2];
  const slash = rest.indexOf("/");
  return slash === -1
    ? { authority: rest, path: "" }
    : { authority: rest.slice(0, slash), path: rest.slice(slash) };
};

/** Did the pasted URL carry its own port (vs. the scheme default)? */
export const hasExplicitPort = (raw: string): boolean => {
  const split = splitAuthority(raw);
  if (!split) return false;
  const hostPort = split.authority.slice(split.authority.lastIndexOf("@") + 1);
  return /:\d+$/.test(hostPort);
};

/**
 * Plain-language hints about a pasted URL, shown with the parse preview
 * (ENG-2042): the RTSPS default port, Genetec's :654, and a "/" in the
 * password (which ends the credentials early unless written as %2F).
 */
export const streamUrlHints = (raw: string): string[] => {
  const hints: string[] = [];
  const split = splitAuthority(raw);
  if (!split) return hints;

  // "user:pa/ss@host/live" → the authority stops at "pa", and the "@" that
  // should end the credentials lands in the path.
  if (
    !split.authority.includes("@") &&
    split.authority.includes(":") &&
    split.path.includes("@")
  ) {
    hints.push(
      'The password seems to contain "/". Write each "/" in the password as %2F, for example rtsp://admin:ab%2Fcd@203.0.113.20:554/live. Otherwise everything after the "/" is read as the stream path.'
    );
  }

  const parsed = parseStreamUrl(raw);
  if (!parsed) return hints;
  if (parsed.protocol === "rtsps" && !hasExplicitPort(raw)) {
    hints.push(
      "This RTSPS URL has no port, so the default port 554 will be used."
    );
  }
  const looksGenetec =
    /genetec/i.test(parsed.host) ||
    /genetec/i.test(parsed.path) ||
    GUID_LIVE_PATH.test(parsed.path);
  if (
    parsed.port !== "654" &&
    (looksGenetec || (parsed.protocol === "rtsps" && parsed.port === "554"))
  ) {
    hints.push(
      "Genetec Media Gateway usually needs :654 in the URL, for example rtsps://user:password@203.0.113.20:654/<camera GUID>/live."
    );
  }
  return hints;
};

/** Does this wizard step ask for a camera stream address? */
export const isStreamAddressStep = (config: any[]): boolean => {
  const fields = new Set((config || []).map((c: any) => c && c.field));
  return fields.has("ip") && fields.has("channel");
};

/**
 * Problems with Host / Stream Parameter the connector cannot recover from,
 * keyed by field. Messages name the field and show a correct example so they
 * work for installers reading English as a second language.
 */
export const streamAddressProblems = (
  values: Record<string, any>
): Record<string, string> => {
  const problems: Record<string, string> = {};
  const host = String(values?.ip ?? "").trim();
  const channel = String(values?.channel ?? "").trim();

  if (host.includes("://") || host.includes("/")) {
    problems.ip =
      "Host is only the address, without rtsp:// or a path. Example: 203.0.113.20";
  } else if (/:\d+$/.test(host)) {
    problems.ip =
      "Put the port in the port field (RTSP port or HTTP port), not in Host. Example: Host 203.0.113.20, RTSP port 554";
  }

  if (channel.includes("://")) {
    problems.channel =
      "Stream Parameter is only the path, not the full URL. Paste the full URL in the box below instead. Example: /01000000-…/live";
  }
  return problems;
};

/**
 * The stream path the camera will be saved with (ENG-2144). Admin's
 * `format_stream_parameters` takes `stream_parameters` when the form has it,
 * else a non-numeric `channel` (the wizard's "Stream Parameter" field), and
 * `cleanup_stream_parameters` puts the leading "/" back. A pasted full URL is
 * left alone so the preview still shows that mistake.
 */
export const streamPathFromValues = (values: Record<string, any>): string => {
  const path = String(
    values?.stream_parameters || values?.channel || ""
  ).trim();
  if (!path || path.startsWith("/") || path.includes("://")) return path;
  return `/${path}`;
};

/**
 * What the connector will dial, credentials left out: `host:port/path`,
 * prefixed with the camera's own protocol when one is chosen (blank means
 * the site default, which the wizard does not know).
 */
export const assembledStreamAddress = (
  values: Record<string, any>
): string | null => {
  const host = String(values?.ip ?? "").trim();
  if (!host) return null;
  // Mobotix-style HTTP camera steps carry `http` as their port field.
  const port = String(values?.rtsp ?? values?.http ?? "").trim();
  const protocol = String(values?.protocol ?? "")
    .trim()
    .toLowerCase();
  return `${protocol ? `${protocol}://` : ""}${host}${
    port ? `:${port}` : ""
  }${streamPathFromValues(values)}`;
};

const PROTOCOL_LABELS: Record<string, string> = {
  rtsp: "RTSP (standard, unencrypted)",
  rtsps: "RTSPS (encrypted, use when the VMS requires TLS, e.g. Genetec)",
  http: "HTTP (web stream)",
  https: "HTTPS (encrypted web stream)",
};

/**
 * Relabel the admin's Protocol options (`[name, value]` tuples) in plain
 * language. The stored value is unchanged; unknown values keep their name.
 */
export const withProtocolLabels = (options: any[]): any[] =>
  (options || []).map((option: any) => {
    if (!Array.isArray(option)) return option;
    const label = PROTOCOL_LABELS[String(option[1] ?? "").toLowerCase()];
    return label ? [label, option[1]] : option;
  });
